// src/components/dashboard/AppointmentsByDayChart.js
import React, { useMemo, useState } from 'react';

/**
 * Appointments by Day of Week Chart
 * Bar chart showing appointment patterns across days of the week
 *
 * @param {Array} data - Array of day data with { day, appointments, completed, noShows }
 */
const AppointmentsByDayChart = ({ data }) => {
  const [hoveredBar, setHoveredBar] = useState(null);

  // Calculate max value for scaling (support both appointments and bookings keys)
  const maxValue = useMemo(() => {
    return Math.max(...data.map(d => d.appointments || d.bookings || 0));
  }, [data]);

  // Calculate height percentage for bars
  const getHeight = (value) => {
    if (maxValue === 0) return 0;
    return (value / maxValue) * 100;
  };

  return (
    <div className="bg-app-bg-secondary-light dark:bg-app-bg-secondary border border-app-border-light dark:border-app-border rounded-2xl p-4 lg:p-6">
      <div className="mb-6">
        <h3 className="text-base lg:text-lg font-semibold text-app-text-light dark:text-app-text">
          Appointments by Day of Week
        </h3>
        <p className="text-xs text-app-text-secondary-light dark:text-app-text-secondary mt-1">
          Weekly appointment patterns
        </p>
      </div>

      {/* Legend */}
      <div className="flex gap-4 mb-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-blue-500"></div>
          <span className="text-app-text-light dark:text-app-text">Total Appointments</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-green-500"></div>
          <span className="text-app-text-light dark:text-app-text">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-orange-500"></div>
          <span className="text-app-text-light dark:text-app-text">No Shows</span>
        </div>
      </div>

      {/* Chart */}
      <div className="grid grid-cols-7 gap-2 lg:gap-3">
        {data.map((day, index) => {
          const appointmentCount = day.appointments || day.bookings || 0;
          const appointmentsHeight = getHeight(appointmentCount);
          const completedHeight = getHeight(day.completed);
          const noShowsHeight = getHeight(day.noShows);
          const isHovered = hoveredBar === index;
          const completionRate = appointmentCount > 0 ? ((day.completed / appointmentCount) * 100).toFixed(1) : 0;

          return (
            <div
              key={day.day}
              className="flex flex-col items-center"
              onMouseEnter={() => setHoveredBar(index)}
              onMouseLeave={() => setHoveredBar(null)}
            >
              {/* Bar container */}
              <div className="relative w-full mb-2" style={{ height: '180px' }}>
                {/* Tooltip on hover */}
                {isHovered && (
                  <div className="absolute -top-24 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-app-bg-secondary-light dark:bg-app-bg-tertiary border border-app-border-light dark:border-app-border rounded-lg p-2 shadow-lg whitespace-nowrap">
                      <div className="text-xs text-app-text-light dark:text-app-text font-semibold mb-1">
                        {day.day}
                      </div>
                      <div className="text-[10px] text-blue-600 dark:text-blue-400">
                        Appointments: {appointmentCount}
                      </div>
                      <div className="text-[10px] text-green-600 dark:text-green-400">
                        Completed: {day.completed}
                      </div>
                      <div className="text-[10px] text-orange-600 dark:text-orange-400">
                        No Shows: {day.noShows}
                      </div>
                      <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary mt-1 pt-1 border-t border-app-border-light dark:border-app-border">
                        Rate: {completionRate}%
                      </div>
                    </div>
                  </div>
                )}

                {/* Bars */}
                <div className="absolute bottom-0 w-full flex gap-0.5 items-end justify-center h-full">
                  {/* Total appointments bar */}
                  <div
                    className={`flex-1 bg-blue-200 dark:bg-blue-500/30 border-2 border-blue-500 rounded-t transition-all ${
                      isHovered ? 'opacity-100' : 'opacity-80'
                    }`}
                    style={{ height: `${appointmentsHeight}%` }}
                  >
                    <div className="flex items-end justify-center h-full pb-1">
                      {appointmentCount > 0 && (
                        <span className="text-[10px] font-semibold text-blue-700 dark:text-blue-200">
                          {appointmentCount}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Completed bar */}
                  <div
                    className={`flex-1 bg-green-200 dark:bg-green-500/30 border-2 border-green-500 rounded-t transition-all ${
                      isHovered ? 'opacity-100' : 'opacity-80'
                    }`}
                    style={{ height: `${completedHeight}%` }}
                  >
                    <div className="flex items-end justify-center h-full pb-1">
                      {day.completed > 0 && (
                        <span className="text-[10px] font-semibold text-green-700 dark:text-green-200">
                          {day.completed}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* No shows bar */}
                  <div
                    className={`flex-1 bg-orange-200 dark:bg-orange-500/30 border-2 border-orange-500 rounded-t transition-all ${
                      isHovered ? 'opacity-100' : 'opacity-80'
                    }`}
                    style={{ height: `${noShowsHeight}%` }}
                  >
                    <div className="flex items-end justify-center h-full pb-1">
                      {day.noShows > 0 && (
                        <span className="text-[10px] font-semibold text-orange-700 dark:text-orange-200">
                          {day.noShows}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Day label */}
              <div className={`text-xs font-medium transition-colors ${
                isHovered ? 'text-blue-600 dark:text-blue-400' : 'text-app-text-secondary-light dark:text-app-text-secondary'
              }`}>
                {day.day}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary stats */}
      <div className="mt-6 pt-4 border-t border-app-border-light dark:border-app-border grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="text-lg font-bold text-blue-600 dark:text-blue-400">
            {data.reduce((sum, d) => sum + (d.appointments || d.bookings || 0), 0)}
          </div>
          <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">Total Appointments</div>
        </div>
        <div>
          <div className="text-lg font-bold text-green-600 dark:text-green-400">
            {data.reduce((sum, d) => sum + d.completed, 0)}
          </div>
          <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">Completed</div>
        </div>
        <div>
          <div className="text-lg font-bold text-orange-600 dark:text-orange-400">
            {data.reduce((sum, d) => sum + d.noShows, 0)}
          </div>
          <div className="text-[10px] text-app-text-secondary-light dark:text-app-text-secondary">No Shows</div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsByDayChart;
